import { useQuery } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiPlantillas } from "@/componentes/kpi/KpiTalento"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { EditorPlantillaTH } from "./EditorPlantillaTH"
import { CATEGORIAS, type PlantillaTH } from "./tipos"

const CATEGORIA = Object.fromEntries(CATEGORIAS.map((c) => [c.valor, c.etiqueta]))

/**
 * Las plantillas de Talento Humano: qué documentos sabe generar la agencia.
 *
 * ⚠ **Una plantilla no se borra: se desactiva.** Los documentos emitidos citan su
 * plantilla y su versión; borrarla dejaría esos documentos sin origen.
 */
export function PlantillasTH() {
  const [editando, setEditando] = useState<PlantillaTH | "nueva" | null>(null)
  const lista = useQuery({
    queryKey: ["plantillas-th", "todas"],
    queryFn: () => api.get<PlantillaTH[]>("/api/v1/plantillas-th"),
  })

  const vista = useRef<HTMLDivElement | null>(null)

  return (
    <div>
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        {!!lista.data?.length && (
          <Exportar
            titulo="Plantillas de Talento Humano"
            subtitulo="Los documentos que la agencia sabe generar"
            contenido={vista}
          />
        )}
        <Boton variante="primario" onClick={() => setEditando("nueva")}>
          <Plus size={15} aria-hidden="true" />
          Nueva plantilla
        </Boton>
      </div>
      <KpiPlantillas />

      {lista.isError ? (
        <EstadoError
          titulo="No se pudieron leer las plantillas."
          error={lista.error}
          onReintentar={() => lista.refetch()}
        />
      ) : (
        <div ref={vista}>
          <Tabla etiqueta="Plantillas de Talento Humano">
            <Cabecera>
              <Th>Código</Th>
              <Th>Nombre</Th>
              <Th>Categoría</Th>
              <Th>Quién la genera</Th>
              <Th derecha={true}>Versión</Th>
            </Cabecera>
            <Cuerpo>
              {lista.isPending ? (
                <TablaVacia>Cargando…</TablaVacia>
              ) : (lista.data ?? []).length === 0 ? (
                <TablaVacia>Todavía no hay plantillas.</TablaVacia>
              ) : (
                (lista.data ?? []).map((p) => (
                  <Fila
                    key={p.id}
                    className="cursor-pointer"
                    atenuada={!p.activa}
                    onClick={() => setEditando(p)}
                  >
                    <Td className="font-mono" data-exportar-valor={p.codigo}>
                      <button
                        type="button"
                        className="foco-cristal underline-offset-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditando(p)
                        }}
                      >
                        {p.codigo}
                      </button>
                    </Td>
                    <Td data-exportar-valor={p.activa ? p.nombre : `${p.nombre} (inactiva)`}>
                      {p.nombre}
                      {!p.activa && (
                        <Insignia className="ml-2" tono="neutro">
                          Inactiva
                        </Insignia>
                      )}
                    </Td>
                    <Td>{CATEGORIA[p.categoria]}</Td>
                    <Td>
                      {p.uso === "EMPLEADO"
                        ? p.requiere_aprobacion
                          ? "Empleado · con aprobación"
                          : "Empleado"
                        : "Talento Humano"}
                    </Td>
                    <Td derecha={true} numero={true} data-exportar-valor={String(p.version)}>
                      v{p.version}
                    </Td>
                  </Fila>
                ))
              )}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      {editando !== null && (
        <EditorPlantillaTH
          plantilla={editando === "nueva" ? null : editando}
          onCerrar={() => setEditando(null)}
        />
      )}
    </div>
  )
}
